import { useEffect } from "react";
import { motion } from "framer-motion";
import { euro, imgSrc } from "@/lib/api";
import { track } from "@/lib/tracking";

const seen = new Set();

export const RemovedStep = ({ entry, num, onRestore }) => (
  <div className="flex items-center justify-between py-6 border-t border-[#E6DFD5]" data-testid={`removed-step-${entry.step_key}`}>
    <p className="text-sm text-[#8C8275]"><span className="font-serif italic mr-3">{num}</span>{entry.step_name} — rimosso dalla tua routine</p>
    <button data-testid={`restore-step-${entry.step_key}`} className="fs-btn-ghost" onClick={onRestore}>Ripristina</button>
  </div>
);

const MISSING_HINTS = {
  cleanse1: "Usa un olio o balsamo struccante delicato per sciogliere trucco e protezione solare.",
  cleanse: "Usa un detergente delicato in gel o schiuma, senza profumo e senza attivi esfolianti.",
  tone: "Un tonico idratante senza alcol riequilibra la pelle dopo la detersione.",
  moisturize: "Una crema idratante semplice, con ceramidi o acido ialuronico, protegge la barriera.",
  protect: "Non saltare la protezione: SPF 50 ogni mattina, anche in casa.",
  mask: "Una maschera in tessuto idratante una o due volte a settimana.",
};

export const MissingStep = ({ entry, num }) => (
  <div className="py-8 border-t border-[#E6DFD5]" data-testid={`missing-step-${entry.step_key}`}>
    <div className="flex items-baseline gap-4">
      <span className="font-serif italic text-[#C5A059] text-2xl">{num}</span>
      <div>
        <p className="fs-eyebrow">{entry.step_label} · {entry.step_name}</p>
        <p className="text-sm text-[#8C8275] mt-1">{entry.step_description}</p>
      </div>
    </div>
    <div className="mt-4 ml-10 border border-dashed border-[#D9CFC0] bg-[#FDFBF8] p-4">
      <p className="text-sm text-[#1A1A18]">Al momento nessun prodotto del catalogo è adatto al tuo profilo per questo step.</p>
      <p className="text-sm text-[#595652] mt-1 font-light">{MISSING_HINTS[entry.step_key] || "Continua con un prodotto delicato che già usi: il resto della routine resta valido."} Chiedi a Sveta, la tua Beauty Specialist, qui sotto se vuoi un consiglio.</p>
    </div>
  </div>
);

export const StepCard = ({ entry, product, num, skinName, onAlternatives, onRemove, onAddons, extrasCount = 0 }) => {
  useEffect(() => {
    if (seen.has(product.id)) return;
    seen.add(product.id);
    track("product_viewed", { product_id: product.id, product_name: product.name, step: entry.step_key });
  }, [product.id, product.name, entry.step_key]);

  const reasons = product.matched_labels || [];
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="py-10 border-t border-[#E6DFD5] grid sm:grid-cols-12 gap-6 sm:gap-10"
      data-testid={`routine-step-${entry.step_key}`}
    >
      <div className="sm:col-span-12 flex items-baseline gap-4">
        <span className="font-serif italic text-[#C5A059] text-2xl">{num}</span>
        <div>
          <p className="fs-eyebrow">{entry.step_label} · {entry.step_name}</p>
          {entry.optional && <span data-testid={`optional-badge-${entry.step_key}`} className="inline-flex items-center gap-1.5 mt-2 text-[10px] tracking-[0.18em] uppercase text-[#8C6F3E] bg-[#FBF3E3] border border-[#EBD9B4] rounded-full px-2.5 py-1">Consigliato · non obbligatorio</span>}
          <p className="text-sm text-[#8C8275] mt-1">{entry.step_description}</p>
        </div>
      </div>
      <div className="sm:col-span-4">
        <div className="aspect-[4/5] bg-[#F2ECE4] overflow-hidden">
          {product.image && <img src={imgSrc(product.image)} alt={product.name} className="w-full h-full object-cover" />}
        </div>
      </div>
      <div className="sm:col-span-8">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#8C8275]">{product.brand}</p>
        <h3 data-testid={`step-product-name-${entry.step_key}`} className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">{product.name}</h3>
        <p className="text-sm text-[#595652] mt-2 tabular-nums">{euro(product.price)}</p>
        <div className="mt-6">
          <p className="fs-eyebrow text-[#8C6F3E]">Perché è adatto a te</p>
          <p className="text-[15px] leading-relaxed text-[#1A1A18] mt-2 font-light">
            {entry.rinse_off_pick ? <>Detergente a risciacquo: resta sulla pelle pochi secondi, per questo lo proponiamo anche con il tuo profilo{reasons.length ? <>, scelto per {reasons.join(", ").toLowerCase()}</> : null}. </>
              : entry.universal_pick ? <>Formula universale, delicata e adatta a ogni tipo di pelle{reasons.length ? <>, scelta per {reasons.join(", ").toLowerCase()}</> : null}. </>
              : skinName && <>Scelto per la tua <em className="font-serif">{skinName.toLowerCase()}</em>{reasons.length ? <> e per {reasons.join(", ").toLowerCase()}</> : null}. </>}
            {product.why_text}
          </p>
        </div>
        {product.how_text && (
          <div className="mt-5">
            <p className="fs-eyebrow">Come si usa</p>
            <p className="text-sm leading-relaxed text-[#595652] mt-2">{product.how_text}</p>
          </div>
        )}
        <div className="mt-7 flex flex-wrap gap-6">
          {entry.candidates.length > 1 && (
            <button data-testid={`open-alternatives-${entry.step_key}`} className="fs-btn-ghost text-[#1A1A18] border-[#C5A059]" onClick={onAlternatives}>
              Alternative compatibili ({entry.candidates.length - 1})
            </button>
          )}
          {entry.addon_ids?.length > 0 && (
            <button data-testid={`open-addons-${entry.step_key}`} className="fs-btn-ghost text-[#8C6F3E] border-[#C5A059]/60" onClick={onAddons}>
              {entry.addon_kind === "patch" ? "Patch occhi" : entry.addon_kind === "boost" ? "Potenzia il trattamento" : entry.addon_kind === "tonepad" ? "Potenzia con un pad" : "Potenzia con un device"}{extrasCount ? ` · ${extrasCount} aggiunto${extrasCount > 1 ? "i" : ""}` : ` (${entry.addon_ids.length})`}
            </button>
          )}
          <button data-testid={`remove-product-${entry.step_key}`} className="fs-btn-ghost" onClick={onRemove}>Rimuovi</button>
        </div>
      </div>
    </motion.article>
  );
};
